import { fmtEuro } from '../lib/format';

export function HBar({ data, color = 'var(--accent)' }: {
  data: { label: string; value: number; dot?: string }[];
  color?: string;
}) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {data.map((d) => (
        <div key={d.label} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 40px', gap: 12, alignItems: 'center' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--text-secondary)' }}>
            {d.dot && <span className="sec-dot" style={{ background: d.dot }} />}
            {d.label}
          </div>
          <div className="bar-track" style={{ height: 10 }}>
            <div className="bar-fill" style={{ width: `${(d.value / max) * 100}%`, background: d.dot || color }} />
          </div>
          <div className="t-num" style={{ textAlign: 'right', fontWeight: 500, fontSize: 13 }}>{d.value}</div>
        </div>
      ))}
    </div>
  );
}

export function Histogram({ data, color = 'var(--accent)', height = 180 }: {
  data: { label: string; value: number }[];
  color?: string;
  height?: number;
}) {
  const max = Math.max(1, ...data.map((d) => d.value));
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 12, height, padding: '0 4px' }}>
      {data.map((d) => (
        <div key={d.label} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <div className="t-num" style={{ fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 }}>{d.value}</div>
          <div
            style={{
              width: '100%', height: (d.value / max) * (height - 50),
              background: color, borderRadius: '4px 4px 2px 2px',
              minHeight: 4, opacity: 0.9,
            }}
          />
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{d.label}</div>
        </div>
      ))}
    </div>
  );
}

export function Donut({ data, size = 160, thickness = 22 }: {
  data: { color: string; name: string; value: number }[];
  size?: number;
  thickness?: number;
}) {
  const total = data.reduce((s, d) => s + d.value, 0) || 1;
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  let offset = 0;
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="var(--bg-muted)" strokeWidth={thickness} />
      {data.map((d, i) => {
        const len = (d.value / total) * c;
        const el = (
          <circle
            key={i}
            cx={size / 2} cy={size / 2} r={r}
            fill="none"
            stroke={d.color}
            strokeWidth={thickness}
            strokeDasharray={`${len} ${c - len}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${size / 2} ${size / 2})`}
            style={{ transition: 'all 400ms cubic-bezier(0.16,1,0.3,1)' }}
          />
        );
        offset += len;
        return el;
      })}
      <text x={size / 2} y={size / 2 - 4} textAnchor="middle" fontSize="22" fontWeight="600" fill="var(--text-primary)">
        {total}
      </text>
      <text x={size / 2} y={size / 2 + 14} textAnchor="middle" fontSize="11" fill="var(--text-tertiary)">
        gesamt
      </text>
    </svg>
  );
}

export function LineChart({ data, height = 220 }: { data: { m: string; count: number }[]; height?: number }) {
  if (data.length === 0) return null;
  const width = 1000;
  const pad = { l: 38, r: 16, t: 18, b: 30 };
  const w = width - pad.l - pad.r;
  const h = height - pad.t - pad.b;
  const min = Math.min(...data.map((d) => d.count));
  const max = Math.max(...data.map((d) => d.count));
  const range = max - min || 1;
  const padMin = Math.floor(min - range * 0.15);
  const padMax = Math.ceil(max + range * 0.15);
  const yRange = padMax - padMin;
  const pts = data.map((d, i) => {
    const x = pad.l + (i / Math.max(1, data.length - 1)) * w;
    const y = pad.t + h - ((d.count - padMin) / yRange) * h;
    return { x, y, d };
  });
  const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const area = `${path} L${pts[pts.length - 1]!.x},${pad.t + h} L${pts[0]!.x},${pad.t + h} Z`;
  const ticks = 4;
  return (
    <svg width="100%" height={height} viewBox={`0 0 ${width} ${height}`} preserveAspectRatio="none">
      {[...Array(ticks + 1)].map((_, i) => {
        const y = pad.t + (i / ticks) * h;
        const val = Math.round(padMax - (i / ticks) * yRange);
        return (
          <g key={i}>
            <line x1={pad.l} y1={y} x2={pad.l + w} y2={y} stroke="var(--border-subtle)" />
            <text x={pad.l - 8} y={y + 3} fontSize="11" fill="var(--text-tertiary)" textAnchor="end">{val}</text>
          </g>
        );
      })}
      <path d={area} fill="var(--accent)" opacity="0.1" />
      <path d={path} fill="none" stroke="var(--accent)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {pts.map((p, i) => <circle key={i} cx={p.x} cy={p.y} r="3" fill="white" stroke="var(--accent)" strokeWidth="1.5" />)}
      {pts.map((p, i) => {
        const [y, m] = p.d.m.split('-');
        const label = new Date(Number(y), Number(m) - 1, 1).toLocaleDateString('de-AT', { month: 'short' }) + ` ${y}`;
        return <text key={`l${i}`} x={p.x} y={height - 10} fontSize="11" fill="var(--text-tertiary)" textAnchor="middle">{label}</text>;
      })}
    </svg>
  );
}

export function GroupedBars({ data }: { data: { jahr: string; ein: number; aus: number }[] }) {
  const max = Math.max(1, ...data.flatMap((d) => [d.ein, d.aus]));
  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18, height: 170, padding: '0 4px' }}>
        {data.map((d) => (
          <div key={d.jahr} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
            <div style={{ display: 'flex', gap: 5, alignItems: 'flex-end', height: 120, width: '100%', justifyContent: 'center' }}>
              <div style={{ width: 16, height: `${(d.ein / max) * 100}%`, background: 'var(--success)', borderRadius: '3px 3px 0 0', minHeight: 4 }} title={`${d.ein} Eintritte`} />
              <div style={{ width: 16, height: `${(d.aus / max) * 100}%`, background: 'var(--text-quaternary)', borderRadius: '3px 3px 0 0', minHeight: 4 }} title={`${d.aus} Austritte`} />
            </div>
            <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{d.jahr}</div>
          </div>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 16, justifyContent: 'center', marginTop: 10, fontSize: 12.5 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--success)' }} /> Eintritte
        </span>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ width: 10, height: 10, borderRadius: 3, background: 'var(--text-quaternary)' }} /> Austritte
        </span>
      </div>
    </div>
  );
}

export function RevenueBars({ data }: { data: { jahr: number; val: number }[] }) {
  const max = Math.max(1, ...data.map((d) => d.val));
  const thisYear = new Date().getFullYear();
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 18, height: 180, padding: '0 4px' }}>
      {data.map((d) => (
        <div key={d.jahr} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6 }}>
          <div className="t-num" style={{ fontSize: 12.5, fontWeight: 500 }}>{fmtEuro(d.val)}</div>
          <div
            style={{
              width: '60%', maxWidth: 60,
              height: `${(d.val / max) * 120}px`,
              background: 'var(--accent)', borderRadius: '5px 5px 2px 2px',
              minHeight: 6, opacity: d.jahr === thisYear ? 0.55 : 0.95,
            }}
          />
          <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>{d.jahr}{d.jahr === thisYear ? '*' : ''}</div>
        </div>
      ))}
    </div>
  );
}
